import { useEffect, useState } from 'react';
import { Filter, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import type {
  ICompanyItemGroupedInterface,
  ICompanyItemInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';
import GroupItemsDialog from '../GroupItemsDialog';

import DiscountFilter from './DiscountFilter';
import ExcelBtn from './ExcelBtn';
import ItemSelection from './ItemSelection';
import QuantityFilter from './QtyFilter';
import TypeSelection from './TypeSelection';

type Props = {
  setFilterData: React.Dispatch<
    React.SetStateAction<ICompanyItemInterface[] | null>
  >;
  filterData: ICompanyItemInterface[] | null;
  groupedData: ICompanyItemGroupedInterface[] | null;
  allData: ICompanyItemInterface[] | null;
  selectedCompany: ICompanyList | null;
  startDate: Date | null;
  endDate: Date | null;
};

function FilterBox({
  setFilterData,
  allData,
  filterData,
  groupedData,
  endDate,
  selectedCompany,
  startDate,
}: Props) {
  const [items, setItems] = useState<
    { label: string; value: number; isImportant: boolean }[]
  >([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [minQty, setMinQty] = useState<number | ''>('');
  const [maxQty, setMaxQty] = useState<number | ''>('');
  const [minDiscount, setMinDiscount] = useState<number | ''>('');
  const [maxDiscount, setMaxDiscount] = useState<number | ''>('');
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (allData) {
      const uniqueItems = Array.from(
        new Map(
          allData.map((item) => [
            item.ItemID,
            {
              value: item.ItemID,
              label: item.ItemName,
              isImportant: item.ImportantItem,
            },
          ]),
        ).values(),
      );
      setItems(uniqueItems);

      const uniqueTypes = Array.from(new Set(allData.map((item) => item.type)));
      setAllTypes(uniqueTypes);
    }
  }, [allData]);

  useEffect(() => {
    setFilterData(
      allData?.filter(
        (item) =>
          (selectedItems.length === 0 || selectedItems.includes(item.ItemID)) &&
          (selectedTypes.length === 0 || selectedTypes.includes(item.type)) &&
          (minDiscount === '' || item.Discount >= minDiscount) &&
          (maxDiscount === '' || item.Discount <= maxDiscount) &&
          (minQty === '' || item.Qty >= minQty) &&
          (maxQty === '' || item.Qty <= maxQty),
      ) || null,
    );
  }, [
    selectedItems,
    selectedTypes,
    minDiscount,
    maxDiscount,
    minQty,
    maxQty,
    allData,
    setFilterData,
  ]);

  const resetAllFilters = () => {
    setSelectedItems([]);
    setSelectedTypes([]);
    setMinQty('');
    setMaxQty('');
    setMinDiscount('');
    setMaxDiscount('');
  };

  const activeFiltersCount = 
    selectedItems.length + 
    selectedTypes.length + 
    (minQty !== '' ? 1 : 0) + 
    (maxQty !== '' ? 1 : 0) +
    (minDiscount !== '' ? 1 : 0) + 
    (maxDiscount !== '' ? 1 : 0);

  return (
    <div className="w-full">
      <Card className="border-0 bg-gradient-to-br from-background via-background to-muted/20 shadow-xl">
        <CardHeader className="pb-3 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Filter className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">
                  تصفية البيانات
                </CardTitle>
                {activeFiltersCount > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {activeFiltersCount} فلاتر نشطة
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetAllFilters}
                  className="h-8 px-2 text-xs hover:bg-destructive/10 hover:text-destructive"
                >
                  <RefreshCw className="h-3 w-3 ml-1" />
                  إعادة تعيين
                </Button>
              )}
              <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <svg
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Button>
                </CollapsibleTrigger>
              </Collapsible>
            </div>
          </div>
        </CardHeader>
        
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleContent>
            <CardContent className="pt-0 px-3">
              <div className="grid grid-cols-2 gap-2 mb-4">
                {filterData && (
                  <ExcelBtn
                    items={filterData}
                    endDate={endDate}
                    startDate={startDate}
                    selectedCompany={selectedCompany}
                  />
                )}
                <GroupItemsDialog
                  endDate={endDate}
                  startDate={startDate}
                  selectedCompany={selectedCompany}
                  items={groupedData}
                  allTypes={selectedTypes}
                />
              </div>

              <ScrollArea className="h-[calc(70vh-120px)] max-h-[700px] pr-2">
                <div className="space-y-4">
                  <ItemSelection
                    items={items}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                  />
                  
                  <TypeSelection
                    allTypes={allTypes}
                    selectedTypes={selectedTypes}
                    setSelectedTypes={setSelectedTypes}
                  />
                  
                  <div className="grid grid-cols-1 gap-4">
                    <QuantityFilter
                      minQty={minQty}
                      maxQty={maxQty}
                      setMinQty={setMinQty}
                      setMaxQty={setMaxQty}
                      filterData={filterData}
                    />
                    
                    <DiscountFilter
                      minDiscount={minDiscount}
                      maxDiscount={maxDiscount}
                      setMinDiscount={setMinDiscount}
                      setMaxDiscount={setMaxDiscount}
                      filterData={filterData}
                    />
                  </div>
                </div>
              </ScrollArea>

              {filterData && (
                <div className="mt-4 flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <span className="text-sm font-medium">النتائج المطابقة</span>
                  <Badge variant="outline" className="font-bold bg-primary/10 border-primary/30 text-primary">
                    {filterData.length} عنصر
                  </Badge>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
}

export default FilterBox;